import {useEffect, useState} from 'react';

import {merchantService} from '@/services/merchants';
import type {BusinessAPIKeyResponse} from '@/types/AfriexBusiness';
import {getResponseMessage, getThrownMessage} from '@/utils/errorMessage';
import {logRejection} from '@/utils/logRejection';
import {showErrorMessage} from '@/utils/showErrorMessage';

export const useBusinessAPIKeys = (businessId: string) => {
  const [keys, setKeys] = useState<BusinessAPIKeyResponse[]>([]);
  // Starts true and stays true until the business id arrives and the keys
  // load, so the table never flashes its empty state before the fetch.
  const [isLoading, setIsLoading] = useState(true);
  const [hasFailed, setHasFailed] = useState(false);

  useEffect(() => {
    let active = true;
    const fetchKeys = async () => {
      if (!businessId) {
        return;
      }
      try {
        setIsLoading(true);
        setHasFailed(false);
        const data = await merchantService.getBusinessAPIKeys(businessId);
        if (active) {
          setKeys(data);
        }
      } catch (e) {
        if (active) {
          setHasFailed(true);
        }
        showErrorMessage(
          `Failed to load API keys: ${
            getResponseMessage(e) ?? getThrownMessage(e) ?? String(e)
          }`,
        );
      } finally {
        if (active) {
          setIsLoading(false);
        }
      }
    };
    // `fetchKeys` toasts its own failures, so this only fires if that handler
    // itself throws. Log rather than swallow silently.
    fetchKeys().catch(logRejection('Failed to load API keys'));
    return () => {
      active = false;
    };
  }, [businessId]);

  return {keys, isLoading, hasFailed};
};
